// Client-side error log (migration 20260822000400).
//
// Every failure the user can see should leave a row behind, tagged with the
// flow it happened in and the step inside that flow. Before this, an error
// like "Network error during upload" existed only as red text in one tab —
// there was no way to answer "why did my video not publish?" afterwards.
//
// Two rules hold everywhere in this file:
//   1. Logging never throws and never blocks. It is called from catch blocks;
//      a logger that fails there would replace the real error with its own.
//   2. Nothing secret is logged. Upload URIs, signed URLs and tokens are
//      redacted before they reach the database.

import { supabase } from "./supabase";

/** The user-visible journeys. Keep these stable — queries group by them. */
export type Flow =
  | "app"               // uncaught errors, anything outside a named flow
  | "auth"
  | "connection_oauth"
  | "media_validate"
  | "media_convert"
  | "media_upload"
  | "media_import"
  | "post_create"
  | "youtube_upload"
  | "publish_dispatch"
  | "post_view"
  // Deleting a post. Its own flow rather than a step of `post_view`, because
  // the failures worth chasing are specific to it — a delivery the executor
  // claimed mid-delete, a role without `post.delete` — and because a delete
  // that fails is the one action on that screen with nothing to fall back on.
  | "post_delete"
  // Reading the publishing calendar. Separate from post_view because the
  // question asked of the table is different — a window of days rather than one
  // post — and so is the failure worth chasing: an empty month nobody planned.
  | "calendar_view"
  | "delivery_retry"
  // First-run guidance (ADR-0016). Only ever a failed count, which hides a
  // suggestion and nothing else.
  | "engagement"
  // Drafting in the studio (ADR-0024). The server half of this flow lives in
  // the ai-generate function and shares the vocabulary, so one query returns
  // both sides of a draft that did not come back.
  | "ai_generate"
  // Saving a caption for reuse, from Compose. Shares `content_drafts` with the
  // studio but not its flow: this one never touches a model, so a failure here
  // is a write problem and never a generation problem.
  | "content_draft"
  // Not a failure: a plan boundary the user hit. Logged at info level, because
  // "which limit do people run into" is the question pricing is answered with.
  | "paywall";

export type Level = "debug" | "info" | "warn" | "error" | "fatal";

export interface LogFields {
  step?: string;
  level?: Level;
  workspaceId?: string | null;
  postId?: string | null;
  deliveryId?: string | null;
  platform?: string | null;
  runId?: string | null;
  context?: Record<string, unknown>;
}

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? import.meta.env.MODE ?? "dev";

/**
 * One id per run of one flow, so the browser's rows and the Edge Function's
 * rows for the same upload can be read together.
 */
export function newRunId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `run-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  }
}

const SECRET_KEYS =
  /(token|secret|password|authorization|apikey|api_key|signature|upload_uri|signed_url)/i;

/** Query strings on Google/Supabase URLs carry credentials. Keep the shape, drop the payload. */
function redactUrl(value: string): string {
  try {
    const u = new URL(value);
    return `${u.origin}${u.pathname}${u.search ? "?<redacted>" : ""}`;
  } catch {
    return value;
  }
}

function redactString(value: string): string {
  const withoutUrls = value.replace(/https?:\/\/\S+/g, (m) => redactUrl(m));
  return withoutUrls
    .replace(/(access_token|refresh_token|upload_id|apikey|api_key)=[^&\s"']+/gi, "$1=<redacted>")
    .replace(/\b(ey[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,})\b/g, "<jwt>")
    .slice(0, 4000);
}

function redact(value: unknown, depth = 0): unknown {
  if (value == null) return value;
  if (typeof value === "string") return redactString(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (depth >= 4) return "<deep>";
  if (Array.isArray(value)) return value.slice(0, 50).map((v) => redact(v, depth + 1));
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = SECRET_KEYS.test(k) ? "<redacted>" : redact(v, depth + 1);
    }
    return out;
  }
  return String(value);
}

/** Anything can be thrown; pull a message, a name and a code out of it. */
function describe(err: unknown): { message: string; name: string | null; code: string | null } {
  if (err instanceof Error) {
    const anyErr = err as Error & { code?: unknown; status?: unknown };
    return {
      message: err.message || String(err),
      name: err.name || "Error",
      code: anyErr.code != null
        ? String(anyErr.code)
        : anyErr.status != null
        ? String(anyErr.status)
        : null,
    };
  }
  if (typeof err === "object" && err !== null) {
    const o = err as Record<string, unknown>;
    if (typeof o.message === "string") {
      return {
        message: o.message,
        name: typeof o.name === "string" ? o.name : null,
        code: o.code != null ? String(o.code) : null,
      };
    }
  }
  return { message: String(err), name: null, code: null };
}

/** The one write path. Never throws — every caller is already handling a failure. */
async function write(
  flow: Flow,
  message: string,
  fields: LogFields,
  err?: { name: string | null; code: string | null },
  extraContext: Record<string, unknown> = {},
): Promise<number | null> {
  try {
    const { data, error } = await supabase.rpc("log_event", {
      p_flow: flow,
      p_message: redactString(message),
      p_step: fields.step ?? null,
      p_level: fields.level ?? "error",
      p_source: "web",
      p_workspace_id: fields.workspaceId ?? null,
      p_post_id: fields.postId ?? null,
      p_delivery_id: fields.deliveryId ?? null,
      p_platform: fields.platform ?? null,
      p_error_name: err?.name ?? null,
      p_error_code: err?.code ?? null,
      p_run_id: fields.runId ?? null,
      p_context: redact({ ...(fields.context ?? {}), ...extraContext }),
      p_user_agent: typeof navigator === "undefined" ? null : navigator.userAgent,
      p_app_version: APP_VERSION,
    });
    if (error) {
      console.warn("[log] could not write log", error.message);
      return null;
    }
    return (data as number) ?? null;
  } catch (e) {
    // Offline, signed out, or the RPC is missing. The original error still
    // reaches the user through the UI; this one goes no further than the console.
    console.warn("[log] logging failed", e);
    return null;
  }
}

/** A non-error milestone worth keeping — a retry, a resume, a fallback taken. */
export function logEvent(
  flow: Flow,
  message: string,
  fields: LogFields = {},
): Promise<number | null> {
  return write(flow, message, { level: "info", ...fields });
}

/** The common case: something threw, record it against its flow. */
export function logError(
  flow: Flow,
  err: unknown,
  fields: LogFields = {},
): Promise<number | null> {
  const { message, name, code } = describe(err);
  const stack = err instanceof Error && err.stack ? redactString(err.stack).slice(0, 2000) : null;

  console.error(`[${flow}${fields.step ? `/${fields.step}` : ""}]`, err);

  return write(flow, message, fields, { name, code }, {
    stack,
    url: typeof window === "undefined" ? null : window.location.pathname,
  });
}

/**
 * Catch-all for errors no flow handled: React render crashes, listener
 * exceptions, promises nobody awaited. Installed once from main.tsx.
 */
export function installGlobalErrorLogging(): void {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (e) => {
    void logError("app", e.error ?? e.message, {
      step: "window_error",
      level: "fatal",
      context: { filename: e.filename, line: e.lineno, column: e.colno },
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    void logError("app", e.reason, { step: "unhandled_rejection", level: "fatal" });
  });
}
